import React from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippy.js/react';
import colors from 'theme/_colors.scss';
import { IconInfo } from 'svgIcons/MotionPortalIcons';
import './style.scss';

const TableHeaderRows = React.forwardRef((props, ref) => {
    const {
        columns,
        sortConfig,
        onSort,
        filters,
        onFilterChange,
        translate
    } = props;

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return '';
        }
        return sortConfig.direction === 'asc' ? '▲' : '▼';
    };

    const getInfoIcon = (column) => {
        if (!column?.tooltip) { return null; }
        return (
            <Tippy
                content={<span className='table-header-info-tooltip-text'>{column.tooltip}</span>}
                animation='scale'
                theme='light-border table-header-info'
                trigger='click'
                placement='top'
                arrow={true}
                maxWidth={190}
                boundary='viewport'
                appendTo={() => document.body}
                popperOptions={{ modifiers: { flip: { enabled: false } } }}
            >
                <button
                    type='button'
                    className='table-header-info-icon'
                    data-testid={`table-header-info-icon-${column.key}`}
                    aria-label={column.tooltip}
                >
                    <IconInfo width={14} height={14} color={colors.doveGray} />
                </button>
            </Tippy>
        );
    };

    return (
        <div className='table-container' ref={ref}>
            <div className='table-header-title-row'>
                {columns.map((column) =>
                    column.sortable ?
                        <div
                            key={column?.key}
                            className={column?.className}
                        >
                            <button
                                type='button'
                                className='table-header-sort-button'
                                onClick={() => onSort(column.key)}
                            >
                                {column?.title}
                                <span className='sort-icon'>
                                    {getSortIcon(column.key)}
                                </span>
                            </button>
                            {getInfoIcon(column)}
                        </div>
                        :
                        <div
                            key={column.key}
                            className={column?.className}
                        >
                            {column?.title}
                            {getInfoIcon(column)}
                        </div>
                )}
            </div>
            <div className='table-header-filter-row'>
                {columns.map((column) =>
                    <div key={column?.key} className={column?.className}>
                        {column?.filterable ?
                            <div className='filter-input-wrapper'>
                                <input
                                    type='text'
                                    placeholder={translate('ABB.Powertrain.Frontend.selectiontableFilterPlaceholder')}
                                    value={filters[column.key] || ''}
                                    onChange={(e) => onFilterChange(column.key, e.target.value)}
                                />
                                <svg className='filter-icon' width='14' height='14' viewBox='0 0 14 14' fill='none'>
                                    <path d='M0 0h14v2L8.5 7.5v5L5.5 14v-6.5L0 2V0z' fill='currentColor' />
                                </svg>
                            </div>
                            : null}
                    </div>
                )}
            </div>
        </div>
    );
});

TableHeaderRows.displayName = 'TableHeaderRows';

TableHeaderRows.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            title: PropTypes.string,
            className: PropTypes.string.isRequired,
            sortable: PropTypes.bool,
            filterable: PropTypes.bool,
            tooltip: PropTypes.string
        })
    ).isRequired,
    sortConfig: PropTypes.shape({
        key: PropTypes.string,
        direction: PropTypes.string
    }).isRequired,
    onSort: PropTypes.func.isRequired,
    filters: PropTypes.object.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired
};

export default TableHeaderRows;
